import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { CertiCoreMark } from "@/components/CertiCoreMark";
import {
  ArrowRight, Award, ClipboardCheck, Folder, Shield,
  UserCheck, Users, CheckCircle2, Zap, Lock, BarChart3,
} from "lucide-react";
import { useDemoStore, type Role } from "@/store/useDemoStore";

const roleCards: {
  role: Role; title: string; subtitle: string;
  icon: typeof UserCheck; href: string;
  color: string; glow: string; index: number;
}[] = [
  { role: "admin",        title: "Administrador", subtitle: "Estándares, criterios y apertura de generaciones", icon: ClipboardCheck, href: "/admin",        color: "#1a2744", glow: "rgba(26,39,68,0.4)",  index: 0 },
  { role: "instructor",   title: "Instructor",    subtitle: "Grupos de práctica, seguimiento de candidatos",  icon: Users,         href: "/instructor",    color: "#0e7490", glow: "rgba(14,116,144,0.35)", index: 1 },
  { role: "alumno",       title: "Candidato",     subtitle: "Ruta formativa, examen teórico y expediente",    icon: UserCheck,     href: "/alumno",        color: "#0891b2", glow: "rgba(8,145,178,0.35)", index: 2 },
  { role: "evaluador",    title: "Evaluador",     subtitle: "Prechecklist operativo y rúbrica en circuito",   icon: ClipboardCheck,href: "/evaluador",     color: "#16a34a", glow: "rgba(22,163,74,0.35)", index: 3 },
  { role: "dictaminador", title: "Dictaminador",  subtitle: "Revisión de expedientes y emisión de certificado",icon: Folder,       href: "/dictaminador",  color: "#7c3aed", glow: "rgba(124,58,237,0.35)",index: 4 },
  { role: "auditor",      title: "Auditor",       subtitle: "Trazabilidad, inconsistencias y alertas IA",    icon: Shield,        href: "/auditor",       color: "#dc2626", glow: "rgba(220,38,38,0.35)", index: 5 },
];

const processSteps = [
  { n: "01", label: "Publicar estándar", sub: "Admin crea EC y configura criterios" },
  { n: "02", label: "Abrir generación",  sub: "Inscripción de candidatos" },
  { n: "03", label: "Formación",         sub: "Ruta formativa + prácticas en circuito" },
  { n: "04", label: "Evaluación",        sub: "Prechecklist + rúbrica práctica" },
  { n: "05", label: "Dictamen",          sub: "Revisión y emisión de certificado" },
];

const feats = [
  { icon: Zap,          label: "Ciclo completo",   sub: "Del alta al certificado en un solo entorno" },
  { icon: BarChart3,    label: "Trazabilidad total",sub: "Toda la evidencia auditable en tiempo real" },
  { icon: Lock,         label: "Roles estancos",    sub: "Cada perfil ve solo su contexto" },
  { icon: CheckCircle2, label: "Cumplimiento CONOCER",sub: "Alineado al marco EC1781" },
];

export default function LandingPage() {
  const setRole = useDemoStore((s) => s.setRole);
  const [, setLocation] = useLocation();

  return (
    <div style={{ minHeight: "100vh", background: "var(--cc-gray-900)", fontFamily: "var(--cc-font)", overflowX: "hidden" }}>

      {/* ── HERO ── */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        {/* background blobs */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(ellipse 80% 60% at 60% -10%, rgba(14,116,144,0.22) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at -5% 50%, rgba(26,39,68,0.8) 0%, transparent 70%)",
        }} />
        <div style={{
          position: "absolute", top: -120, right: -160, width: 640, height: 640,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(8,145,178,0.12) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        {/* grid overlay */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px", position: "relative" }}>
          {/* nav */}
          <nav style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "28px 0", borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ boxShadow: "0 0 0 1px rgba(8,145,178,0.3), 0 4px 18px rgba(8,145,178,0.25)", borderRadius: 10 }}>
                <CertiCoreMark size={40} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "white", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                  CertiCore
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  Plataforma de certificación
                </div>
              </div>
            </div>
          </nav>

          {/* headline */}
          <div style={{ paddingTop: 56, paddingBottom: 96, maxWidth: 820 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 style={{
                fontSize: "clamp(36px, 6vw, 64px)",
                fontWeight: 900,
                color: "white",
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                marginBottom: 28,
              }}>
                Certificación de
                <br />
                <span style={{
                  background: "linear-gradient(135deg, #38bdf8 0%, #0891b2 40%, #0e7490 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}>
                  estándares de
                </span>
                <br />
                competencias.
              </h1>

              <p style={{
                fontSize: 18, color: "rgba(255,255,255,0.6)",
                lineHeight: 1.65, maxWidth: 620, fontWeight: 400, marginBottom: 40,
              }}>
                Un entorno unificado para operar todo el ciclo — del alta del estándar
                a la entrega del certificado — con seis perfiles, la misma evidencia
                y trazabilidad completa.
              </p>

            </motion.div>
          </div>
        </div>
      </div>

      {/* ── FEATURE PILLS ── */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 32px" }}>
          <div style={{ display: "flex", gap: 0, flexWrap: "wrap" }}>
            {feats.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 28px",
                  borderRight: i < feats.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none",
                  flex: "1 1 200px",
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: "rgba(8,145,178,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon size={15} color="#38bdf8" />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "white", marginBottom: 1 }}>{f.label}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", lineHeight: 1.4 }}>{f.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── PROCESS FLOW ── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "56px 32px 40px" }}>
        <div style={{ marginBottom: 36 }}>
          <div style={{
            display: "inline-block", fontSize: 11, fontWeight: 700,
            color: "rgba(56,189,248,0.8)", letterSpacing: "0.12em",
            textTransform: "uppercase", marginBottom: 12,
          }}>Flujo de certificación</div>
          <h2 style={{
            fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800,
            color: "white", letterSpacing: "-0.025em",
          }}>
            De la apertura al certificado.
          </h2>
        </div>

        <div style={{ display: "flex", gap: 0, position: "relative" }}>
          <div style={{
            position: "absolute", top: 28, left: "10%", right: "10%", height: 1,
            background: "linear-gradient(90deg, transparent, rgba(8,145,178,0.3) 15%, rgba(8,145,178,0.3) 85%, transparent)",
            pointerEvents: "none",
          }} />
          {processSteps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.08 * i }}
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "0 8px" }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: 14, marginBottom: 16,
                background: i === 0 ? "linear-gradient(135deg, #0891b2, #0e7490)" : "rgba(255,255,255,0.05)",
                border: i === 0 ? "none" : "1px solid rgba(255,255,255,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: i === 0 ? "0 4px 16px rgba(8,145,178,0.35)" : "none",
                position: "relative", zIndex: 1,
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: i === 0 ? "white" : "rgba(255,255,255,0.4)", letterSpacing: "-0.02em" }}>{s.n}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? "white" : "rgba(255,255,255,0.7)", marginBottom: 4, letterSpacing: "-0.01em" }}>{s.label}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.45 }}>{s.sub}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── ROLES GRID ── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 32px 0" }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          style={{ marginBottom: 40 }}
        >
          <div style={{
            display: "inline-block", fontSize: 11, fontWeight: 700,
            color: "rgba(56,189,248,0.8)", letterSpacing: "0.12em",
            textTransform: "uppercase", marginBottom: 12,
          }}>Perfiles de acceso</div>
          <h2 style={{
            fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: 800,
            color: "white", letterSpacing: "-0.025em", lineHeight: 1.15,
          }}>
            Seis roles. Un solo flujo.
          </h2>
        </motion.div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
          {roleCards.map((c) => {
            const Icon = c.icon;
            return (
              <motion.button
                key={c.role}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.06 * c.index }}
                whileHover={{ y: -3, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => { setRole(c.role); setLocation(c.href); }}
                data-testid={`card-role-${c.role}`}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.09)",
                  borderRadius: 16, padding: "24px 24px 20px",
                  cursor: "pointer", textAlign: "left",
                  display: "flex", flexDirection: "column", gap: 16,
                  position: "relative", overflow: "hidden",
                  transition: "box-shadow 0.2s",
                  boxShadow: `0 0 0 0 ${c.glow}`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 8px 32px ${c.glow}, 0 0 0 1px ${c.color}44`;
                  (e.currentTarget as HTMLButtonElement).style.borderColor = `${c.color}66`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 0 0 transparent";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.09)";
                }}
              >
                {/* subtle top accent */}
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 2,
                  background: `linear-gradient(90deg, ${c.color}, ${c.color}00)`,
                  borderRadius: "16px 16px 0 0",
                }} />

                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: `${c.color}22`,
                    border: `1px solid ${c.color}44`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon size={20} color={c.color} />
                  </div>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: "rgba(255,255,255,0.05)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <ArrowRight size={13} color="rgba(255,255,255,0.4)" />
                  </div>
                </div>

                <div>
                  <div style={{
                    fontSize: 16, fontWeight: 700, color: "white",
                    letterSpacing: "-0.02em", marginBottom: 6, lineHeight: 1.2,
                  }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>{c.subtitle}</div>
                </div>

                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 12, fontWeight: 600, color: c.color,
                  marginTop: "auto",
                }}>
                  Abrir panel <ArrowRight size={12} />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        padding: "28px 32px",
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 10,
      }}>
        <CertiCoreMark size={28} />
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", fontWeight: 500 }}>
          CertiCore · Plataforma de certificación de estándares de competencias
        </span>
      </div>
    </div>
  );
}
